import { z } from '@hono/zod-openapi'
import { PgUuidSchema } from './common'

export const ConversationStatusSchema = z.enum(['TODO', 'FOLLOW_UP', 'DONE', 'ARCHIVED'])
const ConversationPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT'])
export const ConversationSentimentSchema = z.enum([
  'VERY_POSITIVE',
  'POSITIVE',
  'NEUTRAL',
  'NEGATIVE',
  'VERY_NEGATIVE',
])

const ChannelPropertySchema = z
  .object({
    id: PgUuidSchema,
    workspaceId: z.string(),
    name: z.string(),
    guestDisplayName: z.string().nullable(),
    internalName: z.string().nullable(),
    internalCode: z.string().nullable(),
    address: z.string().nullable(),
  })
  .openapi('ChannelProperty')

const ChannelReservationSchema = z
  .object({
    id: PgUuidSchema,
    externalId: z.string().nullable(),
    confirmationCode: z.string(),
    status: z.string(),
    checkinDate: z.string().openapi({ format: 'date-time' }),
    checkoutDate: z.string().openapi({ format: 'date-time' }),
    checkinTime: z.string().nullable(),
    checkoutTime: z.string().nullable(),
  })
  .openapi('ChannelReservation')

export const ChannelSendBlockedReasonSchema = z
  .enum([
    'OPTED_OUT',
    'RESERVATION_PLACEHOLDER',
    'VOICE_ONLY',
    'INTERNAL_THREAD',
    'PROVIDER_THREAD_UNAVAILABLE',
    'DISCONNECTED',
  ])
  .nullable()

const ChannelSchema = z
  .object({
    id: PgUuidSchema,
    provider: z.string(),
    ota: z.string().nullable(),
    value: z.string(),
    caption: z.string().nullable(),
    isPrimary: z.boolean(),
    isActive: z.boolean(),
    optedOut: z.boolean(),
    sendEnabled: z.boolean(),
    sendBlockedReason: ChannelSendBlockedReasonSchema,
    isGroup: z.boolean(),
    groupName: z.string().nullable(),
    reservationId: PgUuidSchema.nullable(),
    phoneNumberId: PgUuidSchema.nullable(),
    emailAddressId: PgUuidSchema.nullable(),
    integrationTokenId: PgUuidSchema.nullable(),
    property: ChannelPropertySchema.nullable(),
    reservation: ChannelReservationSchema.nullable(),
  })
  .openapi('Channel')

const ContactMethodSchema = z.object({
  id: PgUuidSchema,
  type: z.string(),
  value: z.string(),
  rawValue: z.string().nullable(),
  isPrimary: z.boolean(),
  isActive: z.boolean(),
  hasWhatsapp: z.boolean().nullable(),
})
const ContactCompanySchema = z.object({
  membershipId: PgUuidSchema,
  id: PgUuidSchema,
  role: z.string().nullable(),
  displayName: z.string(),
})
const ContactTypeSchema = z.object({
  id: PgUuidSchema,
  slug: z.string(),
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  aliasOf: z.string().nullable(),
})
const BusinessReservationSchema = ChannelReservationSchema.extend({
  propertyId: PgUuidSchema.nullable(),
  otaReference: z.string().nullable(),
  provider: z.string().nullable(),
  channel: z.string(),
  guestName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  guestCount: z.number().nullable(),
  numberOfAdults: z.number().nullable(),
  numberOfChildren: z.number().nullable(),
  numberOfInfants: z.number().nullable(),
  numberOfPets: z.number().nullable(),
  totalAmount: z.number().nullable(),
  cleaningFee: z.number().nullable(),
  amountPaid: z.number().nullable(),
  currency: z.string().nullable(),
  paymentStatus: z.string(),
  specialRequests: z.string().nullable(),
  guestPortalUrl: z.string().nullable(),
  isPreapproved: z.boolean(),
  notes: z.string().nullable(),
})
const ConversationContactSchema = z
  .object({
    id: PgUuidSchema,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    locale: z.string().nullable(),
    isGroup: z.boolean(),
  })
  .extend({
    contactType: ContactTypeSchema.nullable().optional(),
    methods: z.array(ContactMethodSchema).optional(),
    companies: z.array(ContactCompanySchema).optional(),
  })
  .openapi('ConversationContact')

const TagSchema = z
  .object({
    id: PgUuidSchema,
    name: z.string(),
    color: z.string(),
    icon: z.string().nullable(),
  })
  .openapi('Tag')

const DraftSchema = z
  .object({
    content: z.string(),
    updatedAt: z.string().openapi({ format: 'date-time' }),
  })
  .openapi('ConversationDraft')

const MatchingEventSchema = z
  .object({
    id: PgUuidSchema,
    sourceId: z.string().nullable(),
    type: z.string(),
    direction: z.string().nullable(),
    preview: z.string().nullable(),
    occurredAt: z.string().openapi({ format: 'date-time' }),
  })
  .openapi('MatchingEvent')

const LastEventSchema = z
  .object({
    type: z.string(),
    direction: z.string().nullable(),
    preview: z.string().nullable(),
  })
  .openapi('LastEvent')

export const ConversationSchema = z
  .object({
    hasDeferredProviderHydration: z.boolean().optional(),
    property: z
      .object({
        id: PgUuidSchema,
        name: z.string(),
        internalName: z.string().nullable(),
        internalCode: z.string().nullable(),
      })
      .nullable()
      .optional(),
    reservations: z.array(BusinessReservationSchema).optional(),
    id: PgUuidSchema,
    workspaceId: z.string(),
    contactId: PgUuidSchema,
    subject: z.string().nullable(),
    status: ConversationStatusSchema,
    priority: ConversationPrioritySchema,
    propertyId: PgUuidSchema.nullable(),
    stayId: PgUuidSchema.nullable(),
    reservationId: PgUuidSchema.nullable(),
    assignedUserId: PgUuidSchema.nullable(),
    assignedAgentId: PgUuidSchema.nullable(),
    lastEventAt: z.string().nullable().openapi({ format: 'date-time' }),
    lastEventType: z.string().nullable(),
    lastPreview: z.string().nullable(),
    unreadCount: z.number().int(),
    hasUnread: z.boolean(),
    latestInboundEventId: PgUuidSchema.nullable().optional(),
    reservationContext: z.record(z.string(), z.unknown()).nullable().optional(),
    snoozedUntil: z.string().nullable().openapi({ format: 'date-time' }),
    followUpSetBy: PgUuidSchema.nullable(),
    followUpSetAt: z.string().nullable().openapi({ format: 'date-time' }),
    followUpReason: z.string().nullable(),
    aiPaused: z.boolean(),
    workflowsPaused: z.boolean(),
    aiSummary: z.string().nullable(),
    aiSentiment: ConversationSentimentSchema.nullable(),
    aiSummaryUpdatedAt: z.string().nullable().openapi({ format: 'date-time' }),
    aiSummaryMessageCount: z.number().int().nullable(),
    createdAt: z.string().openapi({ format: 'date-time' }),
    updatedAt: z.string().openapi({ format: 'date-time' }),
    contact: ConversationContactSchema,
    channels: z.array(ChannelSchema),
    lastEvent: LastEventSchema.nullable(),
    tags: z.array(TagSchema),
    commentCount: z.number().int(),
    hasOpenEscalation: z.boolean(),
    matchingEvents: z.array(MatchingEventSchema),
    reservationTags: z.array(TagSchema),
    singleEmailThreadSubject: z.string().nullable(),
    draft: DraftSchema.nullable(),
  })
  .openapi('Conversation')

export const ConversationResponseSchema = z.object({ data: ConversationSchema })
export type ConversationRead = z.infer<typeof ConversationSchema>

const MessageMediaSchema = z
  .object({
    id: PgUuidSchema,
    internalUrl: z.string().nullable(),
    externalUrl: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    filename: z.string().nullable(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    aiCaption: z.string().nullable(),
    metadata: z.unknown(),
    sortOrder: z.number().int(),
  })
  .openapi('MessageMedia')

export const MessageSchema = z
  .object({
    id: PgUuidSchema,
    conversationId: PgUuidSchema.nullable(),
    channelId: PgUuidSchema.nullable(),
    type: z.string(),
    direction: z.string().nullable(),
    actorType: z.string().nullable(),
    actorContactId: PgUuidSchema.nullable(),
    actorUserId: PgUuidSchema.nullable(),
    actorAgentId: PgUuidSchema.nullable(),
    preview: z.string().nullable(),
    status: z.string().nullable(),
    errorMessage: z.string().nullable(),
    occurredAt: z.string().openapi({ format: 'date-time' }),
    payload: z.unknown(),
    channel: z.string(),
    call: z.record(z.string(), z.unknown()).nullable(),
    content: z.string(),
    media: z.array(MessageMediaSchema),
    sourceId: z.string().nullable(),
    sourceSchema: z.string().nullable(),
    sourceTable: z.string().nullable(),
  })
  .openapi('Message')

export const MessagePageSchema = z
  .object({
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  })
  .openapi('Page')

export const ConversationMessagesResponseSchema = z.object({
  data: z.array(MessageSchema),
  page: MessagePageSchema,
})

export const AttachedChannelResponseSchema = z.object({
  data: z.array(
    z.looseObject({
      id: PgUuidSchema,
      provider: z.string(),
      ota: z.string().nullable(),
      value: z.string(),
      caption: z.string().nullable(),
      is_primary: z.boolean(),
      is_active: z.boolean(),
      opted_out: z.boolean(),
      is_group: z.boolean(),
      group_name: z.string().nullable(),
      reservation_id: PgUuidSchema.nullable(),
      external_thread_id: z.string().nullable(),
      sendEnabled: z.boolean(),
      sendBlockedReason: ChannelSendBlockedReasonSchema,
    })
  ),
})
